import { useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState } from 'react';

export default function Canvas({ components, setComponents, setSelectedId, selectedId }) {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [gridSize, setGridSize] = useState(20); // Grid size in pixels
  const [showGrid, setShowGrid] = useState(true); // Show/hide grid
  const [snapToGrid, setSnapToGrid] = useState(true); // Enable/disable snap to grid
  
  // Helper function to snap a value to the nearest grid position
  const snapToGridValue = (value) => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };
  


  // Helper function to get the active tab of a TabControl
  const getActiveTab = (tabControlId) => {
    const tabControl = components.find(c => c.id === tabControlId);
    return tabControl ? (tabControl.properties.activeTab || 0) : 0;
  };

  // Helper function to get tab names from a TabControl
  const getTabNames = (tabControlId) => {
    const tabControl = components.find(c => c.id === tabControlId);
    if (!tabControl || !tabControl.properties.tabNames) return ['Tab 1', 'Tab 2'];
    return tabControl.properties.tabNames.split('\n').filter(name => name.trim());
  };

  // Filter components to show only those that belong to the current active tab
  const getVisibleComponents = () => {
    const visibleComponents = [];
    
    components.forEach(comp => {
      if (comp.type === 'TabControl') {
        // Always show TabControl components
        visibleComponents.push(comp);
      } else if (comp.parentTab) {
        // Check if this component belongs to a TabControl and if that tab is active
        const tabControl = components.find(c => c.id === comp.parentTab);
        if (tabControl && tabControl.type === 'TabControl') {
          const activeTabIndex = getActiveTab(comp.parentTab);
          const componentTabIndex = comp.tabIndex || 0;
          
          if (componentTabIndex === activeTabIndex) {
            visibleComponents.push(comp);
          }
        }
      } else {
        // Components not associated with any tab are always visible
        visibleComponents.push(comp);
      }
    });
    
    return visibleComponents;
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'COMPONENT',
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      const canvasRect = document.querySelector('.canvas-container').getBoundingClientRect();
      
      setComponents(prevComponents => {
        console.log('=== DROP DETECTION DEBUG ===');
        console.log('Drop offset:', offset);
        console.log('Canvas rect:', canvasRect);
        console.log('Available components:', prevComponents.map(c => ({ type: c.type, name: c.name, x: c.properties.x, y: c.properties.y })));
        
        // Find parent Form and TabControl for the drop position
        let parentForm = null;
        let parentTab = null;
        let tabIndex = 0;
        
        prevComponents.forEach(comp => {
          if (comp.type === 'TabControl') {
            const tabControlRect = {
              left: comp.properties.x,
              top: comp.properties.y,
              right: comp.properties.x + comp.properties.width,
              bottom: comp.properties.y + comp.properties.height
            };
            
            const dropX = offset.x - canvasRect.left;
            const dropY = offset.y - canvasRect.top;
            
            if (dropX >= tabControlRect.left && dropX <= tabControlRect.right &&
                dropY >= tabControlRect.top && dropY <= tabControlRect.bottom) {
              parentTab = comp.id;
              tabIndex = comp.properties.activeTab || 0;
            }
          } else if (comp.type === 'Form') {
            const formRect = {
              left: comp.properties.x,
              top: comp.properties.y,
              right: comp.properties.x + comp.properties.width,
              bottom: comp.properties.y + comp.properties.height
            };
            
            const dropX = offset.x - canvasRect.left;
            const dropY = offset.y - canvasRect.top;
            
            console.log('=== FORM DETECTION DEBUG ===');
            console.log('Form:', comp.name, 'at', { x: comp.properties.x, y: comp.properties.y });
            console.log('Form rect:', formRect);
            console.log('Drop position:', { x: dropX, y: dropY });
            console.log('Is drop inside form?', 
              dropX >= formRect.left && dropX <= formRect.right &&
              dropY >= formRect.top && dropY <= formRect.bottom
            );
            
            if (dropX >= formRect.left && dropX <= formRect.right &&
                dropY >= formRect.top && dropY <= formRect.bottom) {
              parentForm = comp.id;
              console.log('Form detected as parent! Setting parentForm to:', comp.id);
            }
            console.log('=== END FORM DETECTION DEBUG ===');
          }
        });
        
        // If no Form was detected but there are Forms available, use the first one
        if (!parentForm) {
          const forms = prevComponents.filter(c => c.type === 'Form');
          if (forms.length > 0) {
            parentForm = forms[0].id;
            console.log('No Form detected at drop position, using first available Form:', forms[0].name, 'ID:', forms[0].id);
          }
        }
        
        console.log('Final parentForm value:', parentForm);
        console.log('=== END DROP DETECTION DEBUG ===');
        
        // Calculate position relative to parent container and snap to grid
        let finalX = snapToGridValue(offset.x - canvasRect.left);
        let finalY = snapToGridValue(offset.y - canvasRect.top);
        
        console.log('=== POSITIONING DEBUG ===');
        console.log('Drop position:', { x: offset.x - canvasRect.left, y: offset.y - canvasRect.top });
        console.log('Parent form:', parentForm);
        
        if (parentForm) {
          const form = prevComponents.find(c => c.id === parentForm);
          if (form) {
            console.log('Form found:', form);
            console.log('Form position:', { x: form.properties.x, y: form.properties.y });
            // Position relative to the Form (Form's top-left is 0,0)
            finalX = snapToGridValue(finalX - form.properties.x);
            finalY = snapToGridValue(finalY - form.properties.y);
            console.log('Final position relative to form:', { x: finalX, y: finalY });
          }
        } else {
          console.log('No parent form detected');
        }
        console.log('=== END POSITIONING DEBUG ===');
        
        // Generate unique component name based on type
        const getUniqueComponentName = (type, currentComponents) => {
          console.log('=== NAME GENERATION DEBUG ===');
          console.log('Type:', type);
          console.log('All components:', currentComponents.map(c => ({ type: c.type, name: c.name })));
          
          const baseName = type.toLowerCase();
          let counter = 1;
          let name = `${baseName}${counter}`;
          
          console.log('Starting with name:', name);
          
          // Find the next available number by checking existing names
          while (currentComponents.some(c => c.name === name)) {
            counter++;
            name = `${baseName}${counter}`;
            console.log('Name exists, trying:', name);
          }
          
          console.log(`Final generated name for ${type}: ${name}`);
          console.log('=== END NAME GENERATION DEBUG ===');
          return name;
        };
        
        const newComponent = {
          id: uuidv4(),
          type: item.type,
          name: getUniqueComponentName(item.type, prevComponents),
          properties: {
            x: finalX,
            y: finalY,
            width: snapToGridValue(100),
            height: snapToGridValue(40),
            text: item.type,
            ...(item.type === 'DateTimePicker' && { value: new Date().toLocaleDateString() }),
          },
          parentTab: parentTab,
          tabIndex: tabIndex,
          parentForm: parentForm
        };
        
        console.log('Creating new component:', newComponent);
        console.log('Position values being stored:', { x: finalX, y: finalY, parentForm });
        console.log('Current components:', prevComponents.map(c => ({ type: c.type, name: c.name })));
        
        // Set the selected ID after creating the component
        setSelectedId(newComponent.id);
        
        return [...prevComponents, newComponent];
      });
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  }));

  const ResizableComponent = ({ comp }) => {
    const componentRef = useRef(null);
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const startPos = useRef({ x: 0, y: 0 });
    const startSize = useRef({ width: 0, height: 0 });

    const handleMouseDown = (e, type) => {
      e.stopPropagation();
      
      if (type === 'drag') {
        isDragging.current = true;
        // Calculate start position based on the component's actual position on screen
        let actualX = comp.properties.x;
        let actualY = comp.properties.y;
        
        if (comp.parentForm) {
          const parentForm = components.find(c => c.id === comp.parentForm);
          if (parentForm) {
            actualX = parentForm.properties.x + comp.properties.x;
            actualY = parentForm.properties.y + comp.properties.y;
          }
        }
        
        startPos.current = { x: e.clientX - actualX, y: e.clientY - actualY };
      } else if (type === 'resize') {
        isResizing.current = true;
        startPos.current = { x: e.clientX, y: e.clientY };
        startSize.current = { width: comp.properties.width, height: comp.properties.height };
      }
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
      if (isDragging.current) {
        const newX = e.clientX - startPos.current.x;
        const newY = e.clientY - startPos.current.y;
        
        // Calculate final position relative to parent
        let finalX = newX;
        let finalY = newY;
        
        if (comp.parentForm) {
          const parentForm = components.find(c => c.id === comp.parentForm);
          if (parentForm) {
            // Convert absolute position to relative position within the form
            finalX = newX - parentForm.properties.x;
            finalY = newY - parentForm.properties.y;
          }
        }
        
        // Snap to grid
        finalX = snapToGridValue(finalX);
        finalY = snapToGridValue(finalY);
        
        setComponents(prevComponents => 
          prevComponents.map(c => 
            c.id === comp.id 
              ? { ...c, properties: { ...c.properties, x: finalX, y: finalY } }
              : c
          )
        );
      } else if (isResizing.current) {
        const deltaX = e.clientX - startPos.current.x;
        const deltaY = e.clientY - startPos.current.y;
        
        const newWidth = Math.max(20, startSize.current.width + deltaX);
        const newHeight = Math.max(20, startSize.current.height + deltaY);
        
        // Snap size to grid
        const snappedWidth = snapToGridValue(newWidth);
        const snappedHeight = snapToGridValue(newHeight);
        
        setComponents(prevComponents => 
          prevComponents.map(c => 
            c.id === comp.id 
              ? { ...c, properties: { ...c.properties, width: snappedWidth, height: snappedHeight } }
              : c
          )
        );
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    return (
      <div
        ref={componentRef}
        style={{
          position: 'absolute',
          left: (() => {
            if (comp.parentForm) {
              const parentForm = components.find(c => c.id === comp.parentForm);
              return parentForm ? parentForm.properties.x + comp.properties.x : comp.properties.x;
            }
            return comp.properties.x;
          })(),
          top: (() => {
            if (comp.parentForm) {
              const parentForm = components.find(c => c.id === comp.parentForm);
              return parentForm ? parentForm.properties.y + comp.properties.y : comp.properties.y;
            }
            return comp.properties.y;
          })(),
          width: comp.properties.width,
          height: comp.properties.height,
          cursor: 'move',
          border: '1px solid #ccc',
          borderRadius: '4px',
          backgroundColor: 'white',
        }}
        onClick={() => setSelectedId(comp.id)}
        onMouseDown={(e) => {
          // Don't start dragging if clicking on interactive elements
          if (e.target.closest('.interactive-element')) {
            return;
          }
          handleMouseDown(e, 'drag');
        }}
      >
        {renderComponent(comp)}
        
        {/* Tab indicator */}
        {comp.parentTab && (() => {
          const tabControl = components.find(c => c.id === comp.parentTab);
          if (tabControl) {
            const tabNames = getTabNames(comp.parentTab);
            const tabIndex = comp.tabIndex || 0;
            const tabName = tabNames[tabIndex] || `Tab ${tabIndex + 1}`;
            return (
              <div
                style={{
                  position: 'absolute',
                  top: -20,
                  left: 0,
                  fontSize: '10px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  whiteSpace: 'nowrap',
                }}
              >
                {tabName}
              </div>
            );
          }
          return null;
        })()}
        
        {/* Form indicator */}
        {comp.parentForm && (() => {
          const parentForm = components.find(c => c.id === comp.parentForm);
          if (parentForm) {
            return (
              <div
                style={{
                  position: 'absolute',
                  top: comp.parentTab ? -40 : -20,
                  left: 0,
                  fontSize: '10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  whiteSpace: 'nowrap',
                }}
              >
                Form: {parentForm.name}
              </div>
            );
          }
          return null;
        })()}
        
        <div
          style={{
            position: 'absolute',
            right: -5,
            bottom: -5,
            width: 10,
            height: 10,
            backgroundColor: '#007bff',
            border: '1px solid white',
            borderRadius: '2px',
            cursor: 'se-resize',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'resize')}
        />
      </div>
    );
  };

  const renderComponent = (comp) => {
    const baseClasses = "w-full h-full flex items-center justify-center rounded cursor-move border";
    
    // Helper function to get background color style
    const getBackgroundColor = (comp) => {
      if (comp.properties.backgroundColor && comp.properties.backgroundColor !== 'default') {
        switch (comp.properties.backgroundColor) {
          case 'White': return '#ffffff';
          case 'LightGray': return '#f3f4f6';
          case 'LightBlue': return '#dbeafe';
          case 'LightGreen': return '#dcfce7';
          case 'LightYellow': return '#fef3c7';
          case 'LightPink': return '#fce7f3';
          case 'Transparent': return 'transparent';
          default: return null;
        }
      }
      return null;
    };
    
    // Helper function to get text color style
    const getTextColor = (comp) => {
      if (comp.properties.textColor && comp.properties.textColor !== 'default') {
        switch (comp.properties.textColor) {
          case 'Black': return '#000000';
          case 'White': return '#ffffff';
          case 'Red': return '#dc2626';
          case 'Blue': return '#2563eb';
          case 'Green': return '#16a34a';
          case 'Gray': return '#6b7280';
          default: return null;
        }
      }
      return null;
    };
    
    // Helper function to get border style
    const getBorderStyle = (comp) => {
      if (comp.properties.borderStyle && comp.properties.borderStyle !== 'default') {
        switch (comp.properties.borderStyle) {
          case 'None': return 'none';
          case 'Solid': return 'solid';
          case 'Dashed': return 'dashed';
          case 'Dotted': return 'dotted';
          default: return null;
        }
      }
      return null;
    };
    
    switch (comp.type) {
      case 'Form':
        const formBackgroundColor = comp.properties.backgroundColor || 'bg-gray-50';
        return (
          <div 
            className={`${baseClasses} text-black border-gray-400 relative`}
            style={{ backgroundColor: comp.properties.backgroundColor || '#f9fafb' }}
          >
            <div 
              className="absolute -top-2 left-2 px-1 text-xs"
              style={{ backgroundColor: comp.properties.backgroundColor || '#f9fafb' }}
            >
              {comp.properties.formTitle || comp.properties.text || 'Form'}
            </div>
            <div className="w-full h-full p-2 text-xs text-gray-500">
              Form Container
            </div>
          </div>
        );
      case 'Button':
        const buttonBgColor = getBackgroundColor(comp) || '#3b82f6';
        const buttonTextColor = getTextColor(comp) || '#ffffff';
        return (
          <div 
            className={`${baseClasses} hover:opacity-80`}
            style={{ 
              backgroundColor: buttonBgColor,
              color: buttonTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            {comp.properties.text || 'Button'}
          </div>
        );
      case 'Label':
        const labelBgColor = getBackgroundColor(comp) || 'transparent';
        const labelTextColor = getTextColor(comp) || '#000000';
        return (
          <div 
            className={`${baseClasses} border-gray-300`}
            style={{ 
              backgroundColor: labelBgColor,
              color: labelTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            {comp.properties.text || 'Label'}
          </div>
        );
      case 'TextBox':
        const textBoxBgColor = getBackgroundColor(comp) || '#ffffff';
        const textBoxTextColor = getTextColor(comp) || '#000000';
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: textBoxBgColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <input 
              type="text" 
              className="w-full h-full px-2 border-none outline-none"
              style={{ backgroundColor: 'transparent', color: textBoxTextColor }}
              placeholder={comp.properties.placeholder || 'Enter text...'}
              value={comp.properties.text || ''}
              readOnly
            />
          </div>
        );
      case 'Checkbox':
        const checkboxBgColor = getBackgroundColor(comp) || '#ffffff';
        const checkboxTextColor = getTextColor(comp) || '#000000';
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: checkboxBgColor,
              color: checkboxTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="w-4 h-4" readOnly />
              <span>{comp.properties.text || 'Checkbox'}</span>
            </div>
          </div>
        );
      case 'Dropdown':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <select className="w-full h-full px-2 border-none outline-none bg-transparent" disabled>
              <option>{comp.properties.text || 'Select option...'}</option>
            </select>
          </div>
        );
      case 'ListBox':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full p-1 text-xs">
              <div className="border-b border-gray-300 pb-1">Item 1</div>
              <div className="border-b border-gray-300 py-1">Item 2</div>
              <div className="py-1">Item 3</div>
            </div>
          </div>
        );
      case 'GroupBox':
        return (
          <div className={`${baseClasses} bg-gray-50 text-black border-gray-400 relative`}>
            <div className="absolute -top-2 left-2 bg-gray-50 px-1 text-xs">
              {comp.properties.text || 'Group'}
            </div>
            <div className="w-full h-full p-2 text-xs text-gray-500">
              Group contents
            </div>
          </div>
        );
      case 'RadioButton':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="flex items-center space-x-2">
              <input type="radio" name="radio" className="w-4 h-4" readOnly />
              <span>{comp.properties.text || 'Radio'}</span>
            </div>
          </div>
        );
      case 'ProgressBar':
        return (
          <div className={`${baseClasses} bg-gray-200 text-black border-gray-400 p-1`}>
            <div className="w-full h-full bg-blue-500 rounded relative">
              <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
                {comp.properties.text || '50%'}
              </div>
            </div>
          </div>
        );
      case 'Slider':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400 p-2`}>
            <div className="w-full h-full bg-gray-200 rounded-full relative">
              <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            </div>
          </div>
        );
      case 'DateTimePicker':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div 
              className="w-full h-full px-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 interactive-element"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(comp.id);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // Prevent dragging when clicking on the date picker
              }}
            >
              <span className="text-sm">{comp.properties.value || new Date().toLocaleDateString()}</span>
              <span className="text-gray-400 text-xs">📅</span>
            </div>
          </div>
        );
      case 'NumericUpDown':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-between">
              <span className="text-sm">0</span>
              <div className="flex flex-col">
                <button className="w-4 h-4 text-xs border">▲</button>
                <button className="w-4 h-4 text-xs border">▼</button>
              </div>
            </div>
          </div>
        );
      case 'RichTextBox':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full p-2 text-xs text-gray-600">
              Multi-line text editor...
            </div>
          </div>
        );
      case 'PictureBox':
        return (
          <div className={`${baseClasses} bg-gray-100 text-gray-500 border-gray-400`}>
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-xs">📷 Image</span>
            </div>
          </div>
        );
      case 'TabControl':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full">
              <div className="flex border-b border-gray-300">
                {comp.properties.tabNames ? 
                  comp.properties.tabNames.split('\n').filter(name => name.trim()).map((tabName, index) => (
                    <div 
                      key={index}
                      className={`px-2 py-1 text-xs cursor-pointer hover:bg-gray-100 ${
                        index === (comp.properties.activeTab || 0) ? 'bg-blue-500 text-white' : 'bg-white text-black'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(`Tab clicked: ${tabName.trim()} (index: ${index}) for TabControl: ${comp.name}`);
                        setComponents(prevComponents => 
                          prevComponents.map(c => 
                            c.id === comp.id 
                              ? { ...c, properties: { ...c.properties, activeTab: index } }
                              : c
                          )
                        );
                      }}
                    >
                      {tabName.trim()}
                    </div>
                  ))
                  : (
                    <>
                      <div className="px-2 py-1 bg-blue-500 text-white text-xs">Tab 1</div>
                      <div className="px-2 py-1 text-xs">Tab 2</div>
                    </>
                  )
                }
              </div>
              <div className="p-2 text-xs text-gray-500">
                {comp.properties.tabContent ? 
                  (() => {
                    const contentArray = comp.properties.tabContent.split('|');
                    const activeIndex = comp.properties.activeTab || 0;
                    return contentArray[activeIndex] ? contentArray[activeIndex].trim() : 'Tab content';
                  })()
                  : comp.properties.tabNames ? 
                    `Content for ${comp.properties.tabNames.split('\n').filter(name => name.trim())[comp.properties.activeTab || 0] || 'Tab 1'}` 
                    : 'Tab content'
                }
              </div>
            </div>
          </div>
        );
      case 'TreeView':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full p-1 text-xs">
              <div className="flex items-center">
                <span className="mr-1">▶</span>
                <span>Root</span>
              </div>
              <div className="ml-4">
                <div className="flex items-center">
                  <span className="mr-1">▶</span>
                  <span>Child 1</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'DataGrid':
        const dataGridBgColor = getBackgroundColor(comp) || '#ffffff';
        const dataGridTextColor = getTextColor(comp) || '#000000';
        
        // Parse custom columns or use defaults
        let columns = ['Name', 'Type', 'Size', 'Status'];
        if (comp.properties.columns) {
          columns = comp.properties.columns.split('\n').filter(col => col.trim());
        }
        
        // Generate sample data based on columns
        const generateSampleData = (cols) => {
          const sampleData = [];
          for (let i = 0; i < 4; i++) {
            const row = {};
            cols.forEach((col, index) => {
              switch (col.trim().toLowerCase()) {
                case 'name':
                  row[col] = `Item${i + 1}.txt`;
                  break;
                case 'type':
                  row[col] = ['Text', 'Image', 'Data', 'Document'][i % 4];
                  break;
                case 'size':
                  row[col] = ['2.5 KB', '1.2 MB', '45 KB', '3.1 MB'][i % 4];
                  break;
                case 'status':
                  row[col] = ['Active', 'Ready', 'Processing', 'Complete'][i % 4];
                  break;
                default:
                  row[col] = `Value ${i + 1}`;
              }
            });
            sampleData.push(row);
          }
          return sampleData;
        };
        
        const sampleData = generateSampleData(columns);
        
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: dataGridBgColor,
              color: dataGridTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full p-1 text-xs">
              <div className="grid grid-cols-4 gap-1 font-medium border-b border-gray-300 pb-1">
                {columns.map((col, index) => (
                  <div key={index} className="truncate">{col}</div>
                ))}
              </div>
              {sampleData.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-4 gap-1 border-b border-gray-200 py-1">
                  {columns.map((col, colIndex) => (
                    <div key={colIndex} className="truncate">{row[col]}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      case 'ListView':
        const listViewBgColor = getBackgroundColor(comp) || '#ffffff';
        const listViewTextColor = getTextColor(comp) || '#000000';
        
        // Parse custom columns or use defaults
        let listViewColumns = ['Name', 'Type', 'Size', 'Status'];
        if (comp.properties.columns) {
          listViewColumns = comp.properties.columns.split('\n').filter(col => col.trim());
        }
        
        const listViewSampleData = [
          { Name: 'File1.txt', Type: 'Text', Size: '2.5 KB', Status: 'Active' },
          { Name: 'Image1.jpg', Type: 'Image', Size: '1.2 MB', Status: 'Ready' },
          { Name: 'Data1.csv', Type: 'Data', Size: '45 KB', Status: 'Processing' }
        ];
        
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: listViewBgColor,
              color: listViewTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full p-1 text-xs">
              <div className="grid grid-cols-4 gap-1 font-medium border-b border-gray-300 pb-1">
                {listViewColumns.map((col, index) => (
                  <div key={index} className="truncate">{col}</div>
                ))}
              </div>
              {listViewSampleData.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-4 gap-1 border-b border-gray-200 py-1 hover:bg-gray-100">
                  {listViewColumns.map((col, colIndex) => (
                    <div key={colIndex} className="truncate">{row[col]}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      case 'ComboBox':
        const comboBoxBgColor = getBackgroundColor(comp) || '#ffffff';
        const comboBoxTextColor = getTextColor(comp) || '#000000';
        
        let comboBoxOptions = ['Option 1', 'Option 2', 'Option 3'];
        if (comp.properties.options) {
          comboBoxOptions = comp.properties.options.split('\n').filter(opt => opt.trim());
        }
        
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: comboBoxBgColor,
              color: comboBoxTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full px-2 flex items-center justify-between">
              <span className="text-sm">{comboBoxOptions[0] || 'Select option...'}</span>
              <span className="text-gray-400 text-xs">▼</span>
            </div>
          </div>
        );
      case 'CheckedListBox':
        const checkedListBoxBgColor = getBackgroundColor(comp) || '#ffffff';
        const checkedListBoxTextColor = getTextColor(comp) || '#000000';
        
        let checkedListBoxItems = ['Item 1', 'Item 2', 'Item 3'];
        if (comp.properties.items) {
          checkedListBoxItems = comp.properties.items.split('\n').filter(item => item.trim());
        }
        
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: checkedListBoxBgColor,
              color: checkedListBoxTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full p-1 text-xs">
              {checkedListBoxItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 py-1">
                  <input type="checkbox" className="w-3 h-3" readOnly />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'SplitContainer':
        const splitContainerBgColor = getBackgroundColor(comp) || '#f3f4f6';
        const splitContainerTextColor = getTextColor(comp) || '#000000';
        const orientation = comp.properties.orientation || 'Horizontal';
        const splitterDistance = comp.properties.splitterDistance || 50;
        
        return (
          <div 
            className={`${baseClasses} border-gray-400 relative`}
            style={{ 
              backgroundColor: splitContainerBgColor,
              color: splitContainerTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            {orientation === 'Horizontal' ? (
              <>
                <div className="w-full h-1/2 border-b border-gray-300 flex items-center justify-center text-xs">
                  Panel 1
                </div>
                <div className="absolute left-0 right-0 h-1 w-1 bg-gray-400 cursor-col-resize" style={{ top: `${splitterDistance}%` }}></div>
                <div className="w-full h-1/2 flex items-center justify-center text-xs">
                  Panel 2
                </div>
              </>
            ) : (
              <>
                <div className="w-1/2 h-full border-r border-gray-300 flex items-center justify-center text-xs">
                  Panel 1
                </div>
                <div className="absolute top-0 bottom-0 w-1 h-1 bg-gray-400 cursor-row-resize" style={{ left: `${splitterDistance}%` }}></div>
                <div className="w-1/2 h-full flex items-center justify-center text-xs">
                  Panel 2
                </div>
              </>
            )}
          </div>
        );
      case 'CommandOutput':
        const commandOutputBgColor = getBackgroundColor(comp) || '#1e1e1e';
        const commandOutputTextColor = getTextColor(comp) || '#ffffff';
        
        return (
          <div 
            className={`${baseClasses} border-gray-600`}
            style={{ 
              backgroundColor: commandOutputBgColor,
              color: commandOutputTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full p-2 text-xs font-mono">
              <div className="text-green-400">PS C:\&gt; {comp.properties.defaultCommand || 'Get-Process'}</div>
              <div className="mt-2 text-gray-300">
                <div>Name                 Id  ProcessName</div>
                <div>----                 --  -----------</div>
                <div>chrome              1234 chrome</div>
                <div>explorer            5678 explorer</div>
                <div>powershell          9012 powershell</div>
              </div>
            </div>
          </div>
        );
      case 'ParameterPanel':
        const parameterPanelBgColor = getBackgroundColor(comp) || '#ffffff';
        const parameterPanelTextColor = getTextColor(comp) || '#000000';
        
        return (
          <div 
            className={`${baseClasses} border-gray-400`}
            style={{ 
              backgroundColor: parameterPanelBgColor,
              color: parameterPanelTextColor,
              borderStyle: getBorderStyle(comp) || 'solid'
            }}
          >
            <div className="w-full h-full p-2 text-xs">
              <div className="font-medium mb-2">{comp.properties.commandName || 'Get-Process'} Parameters</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <label className="text-xs">Name:</label>
                  <input type="text" className="flex-1 border p-1 text-xs" placeholder="Process name" readOnly />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-xs">Id:</label>
                  <input type="number" className="flex-1 border p-1 text-xs" placeholder="Process ID" readOnly />
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" className="w-3 h-3" readOnly />
                  <label className="text-xs">IncludeUsername</label>
                </div>
              </div>
            </div>
          </div>
        );
      case 'MenuStrip':
        return (
          <div className={`${baseClasses} bg-gray-100 text-black border-gray-400`}>
            <div className="w-full h-full flex items-center px-2">
              <span className="text-xs mr-2">File</span>
              <span className="text-xs mr-2">Edit</span>
              <span className="text-xs">Help</span>
            </div>
          </div>
        );
      case 'ToolStrip':
        return (
          <div className={`${baseClasses} bg-gray-100 text-black border-gray-400`}>
            <div className="w-full h-full flex items-center px-2 space-x-1">
              <div className="w-6 h-6 bg-blue-500 rounded text-white text-xs flex items-center justify-center">A</div>
              <div className="w-6 h-6 bg-green-500 rounded text-white text-xs flex items-center justify-center">B</div>
              <div className="w-6 h-6 bg-red-500 rounded text-white text-xs flex items-center justify-center">C</div>
            </div>
          </div>
        );
      case 'StatusStrip':
        return (
          <div className={`${baseClasses} bg-gray-200 text-black border-gray-400`}>
            <div className="w-full h-full flex items-center px-2">
              <span className="text-xs">Ready</span>
            </div>
          </div>
        );
      case 'Panel':
        return (
          <div className={`${baseClasses} bg-gray-50 text-black border-gray-400 relative`}>
            <div className="absolute -top-2 left-2 bg-gray-50 px-1 text-xs">
              {comp.properties.text || 'Panel'}
            </div>
            <div className="w-full h-full p-2 text-xs text-gray-500">
              Panel Container
            </div>
          </div>
        );
      case 'Separator':
        return (
          <div className={`${baseClasses} bg-gray-300 text-transparent border-gray-400`}>
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-full h-0.5 bg-gray-400"></div>
            </div>
          </div>
        );
      case 'LinkLabel':
        return (
          <div className={`${baseClasses} bg-transparent text-blue-600 border-transparent cursor-pointer hover:text-blue-800`}>
            <span className="underline">{comp.properties.text || 'Click here'}</span>
          </div>
        );
      case 'MonthCalendar':
        return (
          <div className={`${baseClasses} bg-white text-black border-gray-400`}>
            <div className="w-full h-full p-1 text-xs">
              <div className="text-center font-medium mb-1">December 2023</div>
              <div className="grid grid-cols-7 gap-1 text-center">
                <div className="text-gray-500">S</div>
                <div className="text-gray-500">M</div>
                <div className="text-gray-500">T</div>
                <div className="text-gray-500">W</div>
                <div className="text-gray-500">T</div>
                <div className="text-gray-500">F</div>
                <div className="text-gray-500">S</div>
                <div className="text-gray-400">26</div>
                <div className="text-gray-400">27</div>
                <div className="text-gray-400">28</div>
                <div className="text-gray-400">29</div>
                <div className="text-gray-400">30</div>
                <div>1</div>
                <div>2</div>
                <div>3</div>
                <div>4</div>
                <div>5</div>
                <div>6</div>
                <div>7</div>
                <div>8</div>
                <div>9</div>
                <div className="bg-blue-500 text-white rounded">10</div>
                <div>11</div>
                <div>12</div>
                <div>13</div>
                <div>14</div>
                <div>15</div>
                <div>16</div>
                <div>17</div>
                <div>18</div>
                <div>19</div>
                <div>20</div>
                <div>21</div>
                <div>22</div>
                <div>23</div>
                <div>24</div>
                <div>25</div>
                <div>26</div>
                <div>27</div>
                <div>28</div>
                <div>29</div>
                <div>30</div>
                <div>31</div>
              </div>
            </div>
          </div>
        );
      case 'ColorDialog':
        return (
          <div className={`${baseClasses} bg-blue-500 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">🎨 Color Picker</span>
            </div>
          </div>
        );
      case 'OpenFileDialog':
        return (
          <div className={`${baseClasses} bg-green-500 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">📁 Open File</span>
            </div>
          </div>
        );
      case 'SaveFileDialog':
        return (
          <div className={`${baseClasses} bg-orange-500 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">💾 Save File</span>
            </div>
          </div>
        );
      case 'FolderBrowserDialog':
        return (
          <div className={`${baseClasses} bg-purple-500 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">📂 Browse Folder</span>
            </div>
          </div>
        );
      case 'PrintDialog':
        return (
          <div className={`${baseClasses} bg-gray-600 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">🖨️ Print</span>
            </div>
          </div>
        );
      case 'FontDialog':
        return (
          <div className={`${baseClasses} bg-indigo-500 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">🔤 Font</span>
            </div>
          </div>
        );
      case 'Timer':
        return (
          <div className={`${baseClasses} bg-yellow-500 text-black border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">⏱️ Timer</span>
            </div>
          </div>
        );
      case 'NotifyIcon':
        return (
          <div className={`${baseClasses} bg-gray-700 text-white border-gray-400`}>
            <div className="w-full h-full px-2 flex items-center justify-center">
              <span className="text-sm">🔔 Notify Icon</span>
            </div>
          </div>
        );
      default:
        return (
          <div className={`${baseClasses} bg-gray-500 text-white`}>
            {comp.properties.text || 'Unknown'}
          </div>
        );
    }
  };

  return (
    <div 
      ref={drop}
      className={`flex-1 bg-white border relative overflow-hidden canvas-container ${
        isOver ? 'bg-blue-50 border-blue-300' : ''
      }`}
      style={{
        backgroundImage: showGrid ? `
          linear-gradient(to right, #f0f0f0 1px, transparent 1px),
          linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)
        ` : 'none',
        backgroundSize: `${gridSize}px ${gridSize}px`
      }}
    >
      {/* Grid controls */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '10px',
          borderRadius: '4px',
          border: '1px solid #ccc'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            id="showGrid"
            checked={showGrid}
            onChange={(e) => setShowGrid(e.target.checked)}
            style={{ width: '16px', height: '16px' }}
          />
          <label htmlFor="showGrid" style={{ fontSize: '12px' }}>Show Grid</label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            id="snapToGrid"
            checked={snapToGrid}
            onChange={(e) => setSnapToGrid(e.target.checked)}
            style={{ width: '16px', height: '16px' }}
          />
          <label htmlFor="snapToGrid" style={{ fontSize: '12px' }}>Snap to Grid</label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <label style={{ fontSize: '12px' }}>Grid Size:</label>
          <select
            value={gridSize}
            onChange={(e) => setGridSize(parseInt(e.target.value))}
            style={{ fontSize: '12px', padding: '2px' }}
          >
            <option value={10}>10px</option>
            <option value={20}>20px</option>
            <option value={25}>25px</option>
            <option value={50}>50px</option>
          </select>
        </div>
        <button
          style={{
            padding: '5px 10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          onClick={() => {
            // Clear canvas
            if (window.confirm('Are you sure you want to clear the canvas?')) {
              setComponents([]);
              setSelectedId(null);
            }
          }}
        >
          Clear Canvas
        </button>
        
        {/* Alignment tools */}
        <div style={{ borderTop: '1px solid #ccc', paddingTop: '5px', marginTop: '5px' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Alignment (Select component first):</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <button
              style={{
                padding: '3px 6px',
                backgroundColor: selectedId ? '#6c757d' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '2px',
                cursor: selectedId ? 'pointer' : 'not-allowed',
                fontSize: '10px'
              }}
              onClick={() => {
                // Align selected component to grid
                const selectedComponent = components.find(c => c.id === selectedId);
                if (selectedComponent) {
                  const snappedX = snapToGridValue(selectedComponent.properties.x);
                  const snappedY = snapToGridValue(selectedComponent.properties.y);
                  setComponents(prevComponents => 
                    prevComponents.map(c => 
                      c.id === selectedId 
                        ? { ...c, properties: { ...c.properties, x: snappedX, y: snappedY } }
                        : c
                    )
                  );
                } else {
                  alert('Please select a component first');
                }
              }}
              disabled={!selectedId}
            >
              Align to Grid
            </button>

          </div>
        </div>
      </div>
      
      {getVisibleComponents().map(comp => (
        <ResizableComponent key={comp.id} comp={comp} />
      ))}
    </div>
  );
}